import * as THREE from 'three'
import { WORLD_BOUNDS } from './geo'

const COLUMNS = 46
const ROWS = 30
const DIRECTIONS = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
] as const

export interface TerrainFlightPath {
  getPointAt: (progress: number) => THREE.Vector3
  length: number
}

function indexOf(column: number, row: number): number {
  return row * COLUMNS + column
}

function cellCenter(column: number, row: number): THREE.Vector2 {
  return new THREE.Vector2(
    WORLD_BOUNDS.minX + (column / (COLUMNS - 1)) * WORLD_BOUNDS.width,
    WORLD_BOUNDS.minZ + (row / (ROWS - 1)) * WORLD_BOUNDS.depth,
  )
}

function nearestCell(point: THREE.Vector3): [number, number] {
  return [
    THREE.MathUtils.clamp(
      Math.round(((point.x - WORLD_BOUNDS.minX) / WORLD_BOUNDS.width) * (COLUMNS - 1)),
      0,
      COLUMNS - 1,
    ),
    THREE.MathUtils.clamp(
      Math.round(((point.z - WORLD_BOUNDS.minZ) / WORLD_BOUNDS.depth) * (ROWS - 1)),
      0,
      ROWS - 1,
    ),
  ]
}

function reconstruct(previous: Int32Array, goal: number): number[] {
  const result: number[] = []
  let current = goal
  while (current >= 0) {
    result.push(current)
    current = previous[current]!
  }
  return result.reverse()
}

function routeCells(
  from: THREE.Vector3,
  to: THREE.Vector3,
  getHeight: (x: number, z: number) => number,
): number[] {
  const size = COLUMNS * ROWS
  const gScore = new Float64Array(size)
  const fScore = new Float64Array(size)
  const previous = new Int32Array(size)
  gScore.fill(Number.POSITIVE_INFINITY)
  fScore.fill(Number.POSITIVE_INFINITY)
  previous.fill(-1)

  const [startColumn, startRow] = nearestCell(from)
  const [goalColumn, goalRow] = nearestCell(to)
  const start = indexOf(startColumn, startRow)
  const goal = indexOf(goalColumn, goalRow)
  const open = new Set<number>([start])
  gScore[start] = 0
  fScore[start] = Math.hypot(goalColumn - startColumn, goalRow - startRow)
  const endpointTerrain = Math.max(
    getHeight(from.x, from.z),
    getHeight(to.x, to.z),
  )

  while (open.size > 0) {
    let current = -1
    let best = Number.POSITIVE_INFINITY
    for (const candidate of open) {
      if (fScore[candidate]! < best) {
        best = fScore[candidate]!
        current = candidate
      }
    }
    if (current === goal) return reconstruct(previous, goal)
    open.delete(current)
    if (current < 0) break

    const column = current % COLUMNS
    const row = Math.floor(current / COLUMNS)
    const currentPoint = cellCenter(column, row)
    const currentHeight = getHeight(currentPoint.x, currentPoint.y)

    for (const [dx, dz] of DIRECTIONS) {
      const nextColumn = column + dx
      const nextRow = row + dz
      if (
        nextColumn < 0 ||
        nextColumn >= COLUMNS ||
        nextRow < 0 ||
        nextRow >= ROWS
      ) {
        continue
      }
      const next = indexOf(nextColumn, nextRow)
      const nextPoint = cellCenter(nextColumn, nextRow)
      const nextHeight = getHeight(nextPoint.x, nextPoint.y)
      const distance = Math.hypot(dx, dz)
      const slopePenalty = Math.abs(nextHeight - currentHeight) * 0.75
      const elevationPenalty = Math.max(0, nextHeight - endpointTerrain) * 0.18
      const ridgePenalty = nextHeight > endpointTerrain + 9 ? 32 : 0
      const tentative =
        gScore[current]! + distance + slopePenalty + elevationPenalty + ridgePenalty

      if (tentative >= gScore[next]!) continue
      previous[next] = current
      gScore[next] = tentative
      fScore[next] =
        tentative + Math.hypot(goalColumn - nextColumn, goalRow - nextRow)
      open.add(next)
    }
  }

  return [start, goal]
}

function routePoints(
  from: THREE.Vector3,
  to: THREE.Vector3,
  getHeight: (x: number, z: number) => number,
  clearance: number,
): THREE.Vector3[] {
  const cells = routeCells(from, to, getHeight)
  const points: THREE.Vector3[] = [from.clone()]
  const last = cells.length - 1

  for (let i = 1; i < last; i++) {
    const previous = cells[i - 1]!
    const current = cells[i]!
    const next = cells[i + 1]!
    const previousDirection = [
      (current % COLUMNS) - (previous % COLUMNS),
      Math.floor(current / COLUMNS) - Math.floor(previous / COLUMNS),
    ]
    const nextDirection = [
      (next % COLUMNS) - (current % COLUMNS),
      Math.floor(next / COLUMNS) - Math.floor(current / COLUMNS),
    ]
    const turns =
      previousDirection[0] !== nextDirection[0] ||
      previousDirection[1] !== nextDirection[1]
    if (!turns && i % 4 !== 0) continue

    const column = current % COLUMNS
    const row = Math.floor(current / COLUMNS)
    const point = cellCenter(column, row)
    const journeyProgress = i / last
    const desiredY = THREE.MathUtils.lerp(from.y, to.y, journeyProgress)
    points.push(
      new THREE.Vector3(
        point.x,
        Math.max(desiredY, getHeight(point.x, point.y) + clearance),
        point.y,
      ),
    )
  }
  points.push(to.clone())
  return points
}

export function buildTerrainFlightPath(
  from: THREE.Vector3,
  to: THREE.Vector3,
  getHeight: (x: number, z: number) => number,
  clearance = 4.5,
): TerrainFlightPath {
  const points = routePoints(from, to, getHeight, clearance)
  const curve = new THREE.CatmullRomCurve3(points, false, 'centripetal', 0.35)
  const length = curve.getLength()

  return {
    length,
    getPointAt: (progress) => {
      const point = curve.getPointAt(THREE.MathUtils.clamp(progress, 0, 1))
      point.y = Math.max(point.y, getHeight(point.x, point.z) + clearance)
      return point
    },
  }
}
